import React, { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { AuthUser } from '../../domains/Login/types/users'
import {
  canEditModule,
  canViewModule,
  getDefaultAuthorizedRoute,
  getDefaultIncomeSectionRoute,
  getDefaultPurchaseSectionRoute,
  getIncomeSectionModule,
  getPurchaseSectionModule,
} from '../../utils/moduleAccess'

interface ModuleAccessProps {
  user?: AuthUser
  code: string
  requiredPermission?: 'view' | 'edit'
  fallbackPath?: string
  children: ReactNode
}

const ModuleAccess = ({
  user,
  code,
  requiredPermission = 'view',
  fallbackPath,
  children,
}: ModuleAccessProps) => {
  const isAllowed =
    requiredPermission === 'edit'
      ? canEditModule(user, code)
      : canViewModule(user, code)

  if (!isAllowed) {
    const nextPath = fallbackPath || getDefaultAuthorizedRoute(user) || '/'
    return <Navigate to={nextPath} replace />
  }

  return <>{children}</>
}

interface PurchaseModuleAccessProps {
  user?: AuthUser
  fallbackPath?: string
  children: ReactNode
}

export const PurchaseModuleAccess = ({
  user,
  fallbackPath,
  children,
}: PurchaseModuleAccessProps) => {
  const location = useLocation()
  const requestedModule = getPurchaseSectionModule(location.search)
  const unauthorizedFallback =
    fallbackPath || getDefaultAuthorizedRoute(user) || '/'

  if (canViewModule(user, requestedModule)) {
    return <>{children}</>
  }

  const allowedPurchaseRoute = getDefaultPurchaseSectionRoute(user)

  return (
    <Navigate to={allowedPurchaseRoute || unauthorizedFallback} replace />
  )
}

interface IncomeModuleAccessProps {
  user?: AuthUser
  fallbackPath?: string
  children: ReactNode
}

export const IncomeModuleAccess = ({
  user,
  fallbackPath,
  children,
}: IncomeModuleAccessProps) => {
  const location = useLocation()
  const requestedModule = getIncomeSectionModule(location.search)
  const unauthorizedFallback =
    fallbackPath || getDefaultAuthorizedRoute(user) || '/'

  if (canViewModule(user, requestedModule)) {
    return <>{children}</>
  }

  const allowedIncomeRoute = getDefaultIncomeSectionRoute(user)

  return <Navigate to={allowedIncomeRoute || unauthorizedFallback} replace />
}

export default ModuleAccess
